import React, { useState } from "react";
import {
  useListApplications,
  useUpdateApplication,
  getListApplicationsQueryKey,
  type Application,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/hooks/use-toast";
import { UserRound } from "lucide-react";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: unknown }).data;
    if (data && typeof data === "object" && "error" in data) {
      const message = (data as { error?: unknown }).error;
      if (typeof message === "string" && message.trim()) return message;
    }
  }
  return fallback;
}

function ApplicantAvatar({ name, photoUrl }: { name: string; photoUrl?: string | null }) {
  if (photoUrl) {
    return (
      <img
        src={`/api/storage${photoUrl}`}
        alt={name}
        className="w-9 h-9 rounded-full object-cover border border-gray-200"
      />
    );
  }
  const text = initials(name);
  return (
    <div className="w-9 h-9 rounded-full bg-[#0B132B]/5 border border-gray-200 flex items-center justify-center text-xs font-semibold text-[#0B132B]">
      {text || <UserRound className="w-4 h-4 text-gray-400" />}
    </div>
  );
}

function OpenserveIdCell({ app }: { app: Application }) {
  const current = app.openserveAgentId ?? "";
  const [value, setValue] = useState(current);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { mutate, isPending } = useUpdateApplication();

  const dirty = value.trim() !== current;
  const trimmed = value.trim();
  const prefixOk = trimmed === "" || trimmed.startsWith("MOO");

  const save = () => {
    const next = value.trim();
    if (next && !next.startsWith("MOO")) return;
    mutate(
      { id: app.id, data: { openserveAgentId: next ? next : null } },
      {
        onSuccess: () => {
          toast({
            title: "Openserve Agent ID saved",
            description: next
              ? `Assigned ${next} to ${app.applicantName}.`
              : `Cleared the Openserve Agent ID for ${app.applicantName}.`,
          });
          queryClient.invalidateQueries({ queryKey: getListApplicationsQueryKey() });
        },
        onError: (err) => {
          toast({
            title: "Could not save Openserve Agent ID",
            description: apiErrorMessage(err, "Please try again."),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Not assigned"
          className="h-8 w-44"
          onKeyDown={(e) => {
            if (e.key === "Enter" && dirty && prefixOk && !isPending) save();
          }}
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          disabled={!dirty || !prefixOk || isPending}
          onClick={save}
        >
          Save
        </Button>
      </div>
      {trimmed !== "" && !prefixOk && (
        <span className="text-xs text-amber-600">Must start with &quot;MOO&quot;.</span>
      )}
    </div>
  );
}

export function ApplicationsList() {
  const { data: applicationsPage, isLoading, error } = useListApplications();
  const applications = applicationsPage?.items ?? [];

  if (isLoading) return <div className="p-6 animate-pulse"><div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div><div className="h-64 bg-gray-200 rounded"></div></div>;
  if (error || !applications) return <div className="p-6 text-red-500">Failed to load applications.</div>;

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg text-[#0B132B]">Applications</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead className="w-14">Photo</TableHead>
              <TableHead>Applicant Name</TableHead>
              <TableHead>Agent Type</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Openserve Agent ID</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {applications.map((app) => (
              <TableRow key={app.id}>
                <TableCell>
                  <ApplicantAvatar name={app.applicantName} photoUrl={app.profilePhotoUrl} />
                </TableCell>
                <TableCell className="font-medium">{app.applicantName}</TableCell>
                <TableCell className="capitalize">{app.agentType?.replace('_', ' ') || '-'}</TableCell>
                <TableCell>{app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : '-'}</TableCell>
                <TableCell><StatusBadge status={app.status} /></TableCell>
                <TableCell><OpenserveIdCell app={app} /></TableCell>
              </TableRow>
            ))}
            {applications.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center p-8 text-gray-500">No applications found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
